// src/pages/DiscountsPage.js
import React, { useEffect, useState } from 'react';
import apiClient from '../api/apiClient';
import { Ticket, Loader2, Copy, Clock, Tag, Gift, Share2, Check } from 'lucide-react';
import { convertToPersianNumbers } from '../../src/utils/PersianNumberUtils'; // ← مسیر اصلاح شد

const TOP_BAR_COLORS = [
  'oklch(45.7% 0.24 277.023)',   // بنفش
  'oklch(60.9% 0.126 221.723)',   // آبی
  'oklch(59.6% 0.145 163.225)',   // سبزآبی
  'oklch(66.7% 0.295 322.15)',    // صورتی-بنفش
];

const DiscountsPage = () => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    const fetchDiscounts = async () => {
      try {
        const res = await apiClient.get('/purchases/discount-codes/');
        
        // ============================================================
        // ✅ اصلاح: پشتیبانی از Pagination (results)
        // ============================================================
        let data = res.data?.results || res.data || [];
        if (!Array.isArray(data)) {
          console.warn('⚠️ داده‌های تخفیف آرایه نیستند:', data);
          data = [];
        }

        // ✅ فیلتر کردن کدهای تخفیف با max_uses >= 1
        const activeDiscounts = data.filter(d => d.max_uses >= 1);
        setDiscounts(activeDiscounts);
      } catch (err) {
        console.error('Error fetching discount codes:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDiscounts();
  }, []);

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    }).catch(() => {
      alert('کد تخفیف: ' + code);
    });
  };

  const handleShare = async (discount) => {
    const shareText = `🎁 کد تخفیف فرزانه\nکد: ${discount.code}\n${discount.is_percentage
      ? `${convertToPersianNumbers(discount.value)}٪ تخفیف`
      : `${convertToPersianNumbers(Number(discount.value).toLocaleString())} تومان تخفیف`
    }\nاعتبار تا ${new Date(discount.expiry_date).toLocaleDateString('fa-IR')}\nهمین حالا در farzaneh.app استفاده کنید!`;

    if (navigator.share) {
      try {
        await navigator.share({ title: 'کد تخفیف فرزانه', text: shareText });
      } catch (err) {}
    } else {
      navigator.clipboard.writeText(shareText).then(() => {
        setCopiedCode(discount.code);
        setTimeout(() => setCopiedCode(null), 2000);
      }).catch(() => {
        alert('امکان اشتراک‌گذاری در این مرورگر وجود ندارد. کد تخفیف: ' + discount.code);
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <Loader2 className="animate-spin text-purple-700" size={32} />
      </div>
    );
  }

  return (
    <div className="p-4 max-w-lg mx-auto rtl pb-24">
      {/* هدر */}
      <div className="bg-white text-gray-700 p-4 mb-4 rounded-xl shadow-sm">
        <div className="flex items-center gap-3">
          <Gift size={24} className="text-purple-600" />
          <h1 className="text-[16px] font-extrabold">هدیه فرزانه به شما</h1>
        </div>
        <p className="text-[11px] text-gray-500 mt-1">کدهای تخفیف ویژه برای شما</p>
      </div>

      {discounts.length === 0 ? (
        <div className="text-center text-gray-500 py-10 bg-white rounded-xl shadow">
          <Ticket size={40} className="mx-auto mb-3 text-gray-300" />
          <p>در حال حاضر کد تخفیفی موجود نیست.</p>
          <p className="text-xs mt-1">به‌زودی هدایای ویژه‌ای برای شما در نظر خواهیم گرفت.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {discounts.map((discount, index) => {
            const usedCount = discount.used_count || 0;
            const maxUses = discount.max_uses || 0;
            const remaining = maxUses - usedCount;
            const usagePercent = maxUses > 0 ? (usedCount / maxUses) * 100 : 0;
            const isCopied = copiedCode === discount.code;

            const topBarColor = TOP_BAR_COLORS[index % TOP_BAR_COLORS.length];
            // ایجاد نسخه ۳۰٪ شفاف با فرمت oklch(… / 0.3)
            const gradientStart = topBarColor.replace(')', ' / 0.1)');

            const persianUsedCount = convertToPersianNumbers(usedCount);
            const persianRemaining = convertToPersianNumbers(remaining);
            const persianMaxUses = convertToPersianNumbers(maxUses);
            const persianValue = discount.is_percentage
              ? `٪${convertToPersianNumbers(discount.value)}`
              : `${convertToPersianNumbers(Number(discount.value).toLocaleString())} تومان`;

            return (
              <div
                key={discount.code}
                className="rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-shadow duration-300"
                style={{ background: `linear-gradient(to up, ${gradientStart}, #ffffff)` }}
              >
                <div className="h-2" style={{ backgroundColor: topBarColor }} />

                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag size={20} className="text-purple-600 flex-shrink-0" />
                    <h2 className="text-[16px] font-extrabold text-gray-900 tracking-wide">
                      {discount.code}
                    </h2>
                  </div>

                  <div className="mb-3">
                    <span className="inline-block bg-green-600 text-white px-4 py-1.5 rounded-full text-[13px] font-bold shadow-sm">
                      {persianValue}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                    <Clock size={14} />
                    <span>اعتبار تا {new Date(discount.expiry_date).toLocaleDateString('fa-IR')}</span>
                  </div>

                  {maxUses > 0 && (
                    <div className="mb-4">
                      <div className="flex justify-between text-[10px] text-gray-500 mb-1">
                        <span>{persianUsedCount} استفاده شده</span>
                        <span>{persianRemaining} باقی‌مانده</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${usagePercent > 80 ? 'bg-red-500' : 'bg-green-500'}`}
                          style={{ width: `${Math.min(usagePercent, 100)}%` }}
                        />
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1">از {persianMaxUses} استفاده ممکن</p>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCopyCode(discount.code)}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-[11px] font-semibold transition flex-1 justify-center ${
                        isCopied ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                      }`}
                    >
                      {isCopied ? <Check size={16} /> : <Copy size={16} />}
                      {isCopied ? 'کپی شد' : 'کپی کد'}
                    </button>
                    <button
                      onClick={() => handleShare(discount)}
                      className="flex items-center gap-1.5 bg-gray-100 text-gray-600 px-4 py-2 rounded-lg text-[11px] font-semibold hover:bg-gray-200 transition flex-1 justify-center"
                    >
                      <Share2 size={16} />
                      اشتراک‌گذاری
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-[11px] text-yellow-800">
        <p className="font-semibold mb-1">💡 نحوه استفاده</p>
        <p>کد تخفیف را کپی کنید و هنگام خرید محصول، در قسمت «کد تخفیف» جای‌گذاری نمایید. تخفیف به‌طور خودکار اعمال خواهد شد.</p>
      </div>
    </div>
  );
};

export default DiscountsPage;